// App root: gates the main UI behind the first-run wizard until all
// three credential blocks are configured. Subscribes to pipeline events
// + opacity changes from the Rust side, and renders the global toast.

import { useCallback, useEffect, useState } from "react";
import { firstRunStatus, getCaptureState, getSettings } from "./ipc/commands";
import {
  onAutoTruthToast,
  onFinalGuess,
  onHotkeyRegisterFailures,
  onPipelineResult,
  onPipelineStarted,
  onReverseImageStaleness,
  onRoundReset,
  onSceneChange,
  onSetOpacity,
  onStreamSafety,
} from "./ipc/events";
import { TopBar } from "./components/TopBar";
import { PipelineStack } from "./components/PipelineStack";
import { FinalGuessFooter } from "./components/FinalGuessFooter";
import { FirstRunWizard } from "./components/FirstRunWizard";
import { SettingsPanel } from "./components/SettingsPanel";
import { StatsPanel } from "./components/StatsPanel";
import { Toast } from "./components/Toast";
import { useStore } from "./state/store";
import type { FirstRunStatus } from "./types/contract";

import { useRelay } from "./hooks/useRelay";

function isFullyConfigured(s: FirstRunStatus): boolean {
  return s.openrouter_configured && s.r2_configured && s.geonames_configured;
}

type StartupState =
  | { phase: "loading" }
  | { phase: "ready"; status: FirstRunStatus }
  | { phase: "error"; message: string };

export default function App() {
  useRelay(); // Start the background relay bridge
  
  const [startup, setStartup] = useState<StartupState>({ phase: "loading" });
  const applyStarted = useStore((s) => s.applyStarted);
  const applyResult = useStore((s) => s.applyResult);
  const setFinal = useStore((s) => s.setFinal);
  const setOpacityLocal = useStore((s) => s.setOpacity);
  const setRoundNumber = useStore((s) => s.setRoundNumber);
  const setThumbnail = useStore((s) => s.setThumbnail);
  const toast = useStore((s) => s.toast);
  const dismissToast = useStore((s) => s.dismissToast);
  const streamSafety = useStore((s) => s.streamSafety);
  const setStreamSafety = useStore((s) => s.setStreamSafety);
  const reverseImageStaleness = useStore((s) => s.reverseImageStaleness);
  const setReverseImageStaleness = useStore((s) => s.setReverseImageStaleness);
  const hotkeyState = useStore((s) => s.hotkeyState);
  const setHotkeyState = useStore((s) => s.setHotkeyState);

  const loadStatus = useCallback(
    async (signal: { cancelled: boolean }) => {
      try {
        const s = await firstRunStatus();
        if (signal.cancelled) return;
        setStartup({ phase: "ready", status: s });
        // Hydrate the Zustand store from the Rust-side capture state so
        // a UI hot-reload (or any future hydration scenario) doesn't
        // drop the round number, thumbnail, and elapsed-time indicator.
        // Failures here are non-fatal: the app is still usable, the
        // worst case is the indicators reset to their idle state on
        // the next pipeline event.
        try {
          const cap = await getCaptureState();
          if (signal.cancelled) return;
          // round_number is u32 on the Rust side, so always ≥ 0.
          // No `> 0` guard: if we ever see 0 here, that's a backend
          // bug we want to surface, not silently elide.
          setRoundNumber(cap.round_number);
          setThumbnail(cap.last_thumbnail_path, cap.captured_at_ms);
        } catch (err) {
          // Best-effort hydration; loud-but-non-fatal so a real
          // regression in get_capture_state surfaces in dev console.
          // eslint-disable-next-line no-console
          console.warn("App: getCaptureState hydration failed", err);
        }
      } catch (e) {
        if (!signal.cancelled)
          setStartup({ phase: "error", message: String(e) });
      }
    },
    [setRoundNumber, setThumbnail],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    void loadStatus(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadStatus]);

  // Hydrate the disabled-pipelines list from user.toml at app
  // mount so the PipelineStack hides cards for pipelines the user
  // has disabled. Refreshes again whenever SettingsPanel auto-
  // saves a toggle change.
  useEffect(() => {
    let cancelled = false;
    getSettings()
      .then((s) => {
        if (!cancelled) {
          useStore.getState().setDisabledPipelines(s.disabled_pipelines);
        }
      })
      .catch((e) => {
        // Non-fatal; PipelineStack defaults to "show all" on load
        // failure. Logged at debug for dev console.
        // eslint-disable-next-line no-console
        console.debug("App: disabled-pipelines hydration failed", e);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const unlisten: Array<Promise<() => void>> = [
      onPipelineStarted(applyStarted),
      onPipelineResult(applyResult),
      onFinalGuess((e) =>
        setFinal({
          lat: e.lat,
          lng: e.lng,
          label: e.label,
          source: e.source,
          confidence: e.confidence,
        }),
      ),
      onSetOpacity(setOpacityLocal),
      onRoundReset(() => setRoundNumber(1)),
      onStreamSafety((e) =>
        setStreamSafety({
          streamSafe: e.stream_safe,
          build: e.build,
          minBuild: e.min_build,
          setCallSucceeded: e.set_call_succeeded,
          inRemoteSession: e.in_remote_session,
        }),
      ),
      onReverseImageStaleness((e) =>
        setReverseImageStaleness({
          yandexOk: e.yandex_ok,
          lensOk: e.lens_ok,
          bingOk: e.bing_ok,
        }),
      ),
      onHotkeyRegisterFailures((payload) =>
        setHotkeyState({
          failures: payload.failures,
          registered: payload.registered,
          total: payload.total,
        }),
      ),
      onSceneChange((e) => {
        // Notify-only Phase 3 UX: a toast tells the user a new
        // round (or other significant transition) was detected
        // on the GeoGuessr browser. They still drive Ctrl+Shift+G
        // manually. Auto-fire becomes a Phase 4 toggle.
        useStore
          .getState()
          .showToast(
            `Scene change detected (Δ${e.hamming}/${e.threshold} bits) — fire Ctrl+Shift+G if a new round started`,
            "warn",
          );
      }),
      onAutoTruthToast((msg) => {
        useStore.getState().showToast(msg, "ok");
      }),
    ];
    return () => {
      unlisten.forEach((p) => void p.then((fn) => fn()));
    };
  }, [
    applyStarted,
    applyResult,
    setFinal,
    setOpacityLocal,
    setRoundNumber,
    setStreamSafety,
    setReverseImageStaleness,
    setHotkeyState,
  ]);

  const setStatus = useCallback(
    (s: FirstRunStatus) => setStartup({ phase: "ready", status: s }),
    [],
  );

  const retry = useCallback(() => {
    setStartup({ phase: "loading" });
    void loadStatus({ cancelled: false });
  }, [loadStatus]);

  const staleEngines = reverseImageStaleness
    ? [
        !reverseImageStaleness.yandexOk ? "Yandex" : null,
        !reverseImageStaleness.lensOk ? "Lens" : null,
        !reverseImageStaleness.bingOk ? "Bing" : null,
      ].filter((x): x is string => x !== null)
    : [];
  // Three orthogonal signals OR together to form the "do not stream"
  // condition:
  //   - !streamSafe            : Windows build < 19041
  //   - !setCallSucceeded      : SetWindowDisplayAffinity failed at startup
  //   - inRemoteSession        : RDP/Quick Assist attached (capture
  //                              pipeline bypasses WDA_EXCLUDEFROMCAPTURE
  //                              regardless of the stored flag)
  // The third signal must be polled via GetSystemMetrics(SM_REMOTESESSION) —
  // GetWindowDisplayAffinity only echoes back the stored value and never
  // detects RDP attach.
  const showStreamBanner =
    !!streamSafety &&
    (!streamSafety.streamSafe ||
      !streamSafety.setCallSucceeded ||
      streamSafety.inRemoteSession);
  // The red stream-unsafe banner is the load-bearing anti-cheat
  // warning ("do not stream"). Co-rendering it above the yellow
  // ops-class banners (reverse-image stale, hotkey failures) in a
  // tight flex stack visually looks like one composite warning, and
  // a user can dismiss the red one as part of the same gestalt.
  // Suppress the yellow banners entirely while red is showing — they
  // re-appear once the red condition clears.
  const showStaleBanner = !showStreamBanner && staleEngines.length > 0;
  const showHotkeyBanner =
    !showStreamBanner && (hotkeyState?.failures.length ?? 0) > 0;
  const anyBanner = showStreamBanner || showStaleBanner || showHotkeyBanner;
  return (
    <>
      {anyBanner && (
        <div
          data-testid="banner-stack"
          // gap-2 (not gap-px) so two yellow banners don't blend
          // into a single block — preserves the visual separation
          // between distinct ops warnings.
          className="fixed top-0 inset-x-0 z-40 flex flex-col gap-2"
        >
          {/* Narrow on the local rather than repeating non-null
              assertions. The gating boolean `showStreamBanner` is
              `!!streamSafety && (...)`, but TS can't narrow across
              the boolean. Asserting once into a local removes the
              chain of `!` operators. */}
          {showStreamBanner && streamSafety && (
            <StreamUnsafeBanner
              build={streamSafety.build}
              minBuild={streamSafety.minBuild}
              buildSafe={streamSafety.streamSafe}
              setCallSucceeded={streamSafety.setCallSucceeded}
              inRemoteSession={streamSafety.inRemoteSession}
            />
          )}
          {showStaleBanner && <ReverseImageStaleBanner engines={staleEngines} />}
          {showHotkeyBanner && hotkeyState && (
            <HotkeyFailuresBanner
              failures={hotkeyState.failures}
              registered={hotkeyState.registered}
              total={hotkeyState.total}
            />
          )}
        </div>
      )}
      {renderBody(startup, setStatus, retry)}
      <StatsPanelGate />
      <SettingsPanelGate />
      {toast && (
        <Toast
          id={toast.id}
          message={toast.message}
          kind={toast.kind}
          onDone={dismissToast}
        />
      )}
    </>
  );
}

function StatsPanelGate() {
  const open = useStore((s) => s.statsOpen);
  const setOpen = useStore((s) => s.setStatsOpen);
  if (!open) return null;
  return <StatsPanel onClose={() => setOpen(false)} />;
}

function SettingsPanelGate() {
  const open = useStore((s) => s.settingsOpen);
  const setOpen = useStore((s) => s.setSettingsOpen);
  if (!open) return null;
  return <SettingsPanel onClose={() => setOpen(false)} />;
}

function ReverseImageStaleBanner({ engines }: { engines: string[] }) {
  return (
    <div
      data-testid="reverse-image-stale-banner"
      role="alert"
      className="bg-[color:var(--warn)]/85 text-fg text-xs px-3 py-1.5 shadow-md"
    >
      <strong>Reverse-image probe failed for:</strong> {engines.join(", ")}.
      That engine's URL template may have changed; results from it will be
      empty/misleading until you update <code>[reverse_image]</code> in
      user.toml.
    </div>
  );
}

function HotkeyFailuresBanner({
  failures,
  registered,
  total,
}: {
  failures: { binding: string; spec: string; error: string }[];
  registered: number;
  total: number;
}) {
  // Counters are non-null because the banner is gated on
  // `hotkeyState !== null && failures.length > 0` upstream — when this
  // component renders, both counters are populated by the wire payload.
  return (
    <div
      data-testid="hotkey-failures-banner"
      role="alert"
      className="bg-[color:var(--warn)]/85 text-fg text-xs px-3 py-1.5 shadow-md"
    >
      <strong>{`Hotkey registration (${registered} / ${total} OK):`}</strong>{" "}
      {failures.map((f) => `${f.binding}=${JSON.stringify(f.spec)}`).join(", ")}.
      Other hotkeys still work; fix the spec in <code>user.toml</code> and
      restart.
    </div>
  );
}

function StreamUnsafeBanner({
  build,
  minBuild,
  buildSafe,
  setCallSucceeded,
  inRemoteSession,
}: {
  build: number | null;
  minBuild: number;
  buildSafe: boolean;
  setCallSucceeded: boolean;
  inRemoteSession: boolean;
}) {
  // Three orthogonal failure modes, each surfaced distinctly so the
  // user knows which to fix:
  //   1. build version < 19041 — flag is a no-op; can't be fixed
  //      without upgrading Windows.
  //   2. SetWindowDisplayAffinity returned an error at startup —
  //      virtualisation / screen-recording shim / certain GPU drivers.
  //   3. Currently in an RDP / Quick Assist remote session — the
  //      RDP capture pipeline bypasses WDA_EXCLUDEFROMCAPTURE
  //      regardless of how the flag was stored. Detected via
  //      GetSystemMetrics(SM_REMOTESESSION); GetWindowDisplayAffinity
  //      only echoes the stored value and won't catch live attach.
  //
  // Order matters: report build first (the most fundamental), then
  // Set call result (startup signal), then RDP (live signal).
  const reason = !buildSafe
    ? `Windows build ${build ?? "unknown"} doesn't honour WDA_EXCLUDEFROMCAPTURE (need ≥ ${minBuild}).`
    : !setCallSucceeded
      ? `SetWindowDisplayAffinity failed at startup on this machine (virtualisation / screen-recording shim / GPU driver suspected).`
      : inRemoteSession
        ? `Currently inside an RDP / Quick Assist remote session — the remote capture pipeline ignores WDA_EXCLUDEFROMCAPTURE, so the overlay would leak into the screencast. Disconnect the remote session before streaming.`
        : "";
  return (
    <div
      data-testid="stream-unsafe-banner"
      role="alert"
      className="bg-[color:var(--err)]/85 text-fg text-xs px-3 py-1.5 shadow-md"
    >
      <strong>Stream-capture hiding unavailable.</strong> {reason} The overlay
      will appear in OBS / Discord screen-share / WGC capture. Do not stream
      Atlas on this machine.
    </div>
  );
}

function renderBody(
  startup: StartupState,
  setStatus: (s: FirstRunStatus) => void,
  retry: () => void,
) {
  if (startup.phase === "loading") {
    // Empty shell — keeps the overlay invisible until we know whether to
    // show the wizard or the pipeline stack. Avoids a flash of the main
    // UI before the wizard.
    return <div className="h-full" />;
  }

  if (startup.phase === "error") {
    return (
      <div
        data-testid="startup-error"
        className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-black/85 p-6 text-fg"
      >
        <div className="text-sm font-semibold">Atlas couldn't start</div>
        <div className="text-xs text-fg-muted text-center max-w-md whitespace-pre-wrap">
          {startup.message}
        </div>
        <button
          type="button"
          onClick={retry}
          className="text-xs px-3 py-1 rounded bg-white/10 hover:bg-white/20"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!isFullyConfigured(startup.status)) {
    return (
      <FirstRunWizard status={startup.status} onStatusChange={setStatus} />
    );
  }

  return (
    <div className="flex flex-col h-full">
      <TopBar />
      <div className="flex-1 overflow-auto">
        <PipelineStack />
      </div>
      <FinalGuessFooter />
    </div>
  );
}
